import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Mail, Lock, ArrowRight, Shield } from "lucide-react";
import gpsLogo from "@/GPS-logo-2022.svg";

type LoginStep = 'email' | 'otp' | 'password';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const MAX_OTP_ATTEMPTS = 5;

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Einmal-Codes gibt es nur für interne Konten (@gpsbau.de); externe
    // Konten (z. B. Vertrieb/Massive Wohnbau) melden sich per Passwort an.
    if (!email.endsWith('@gpsbau.de')) {
      setStep('password');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.requestOtp(email);
      if (response.data.success) {
        setStep('otp');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Senden des Codes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (otp.length !== 6) {
      setError('Bitte geben Sie den 6-stelligen Code ein');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.verifyOtp(email, otp);
      if (response.data.success) {
        const { user, tokens } = response.data.data;
        localStorage.setItem("access_token", tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        if (rememberMe) {
          const expires = new Date(Date.now() + 30 * 864e5).toUTCString();
          document.cookie = `gps_remember=1; expires=${expires}; path=/; SameSite=Lax`;
        }
        setTimeout(() => navigate("/"), 100);
      }
    } catch (err: any) {
      const nextAttempt = otpAttempts + 1;
      setOtpAttempts(nextAttempt);
      if (nextAttempt >= MAX_OTP_ATTEMPTS) {
        setError(`Zu viele Fehlversuche. Bitte fordern Sie einen neuen Code an.`);
        setTimeout(() => {
          resetForm();
        }, 2500);
      } else {
        const remaining = MAX_OTP_ATTEMPTS - nextAttempt;
        setError(`${err.response?.data?.message || 'Ungültiger oder abgelaufener Code'} — noch ${remaining} Versuch${remaining !== 1 ? 'e' : ''}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      if (rememberMe) {
        const expires = new Date(Date.now() + 30 * 864e5).toUTCString();
        document.cookie = `gps_remember=1; expires=${expires}; path=/; SameSite=Lax`;
      }
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStep('email');
    setOtp('');
    setPassword('');
    setError('');
    setRememberMe(false);
    setOtpAttempts(0);
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <img src={gpsLogo} alt="GPS" className="w-[150px] h-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === 'email' && 'Anmelden'}
            {step === 'otp' && 'Code eingeben'}
            {step === 'password' && 'Anmelden'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'email' && 'Mit Ihrer E-Mail-Adresse anmelden'}
            {step === 'otp' && `Wir haben einen Code an ${email} gesendet`}
            {step === 'password' && 'Passwort eingeben'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">{error}</div>
        )}

        {step === 'email' && (
          <form className="mt-8 space-y-6" onSubmit={handleRequestOtp}>
            <div>
              <label htmlFor="email" className="sr-only">E-Mail-Adresse</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-lg relative block w-full pl-10 px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 text-base sm:text-sm"
                  placeholder="name@gpsbau.de"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Code anfordern
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep('password')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Anmeldung mit Passwort
              </button>
            </div>
          </form>
        )}

        {step === 'otp' && (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyOtp}>
            <div>
              <label htmlFor="otp" className="sr-only">Code</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="appearance-none rounded-lg relative block w-full pl-10 px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm text-center tracking-widest text-lg"
                  placeholder="000000"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Der Code ist 15 Minuten gültig
              </p>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Anmelden'}
              </button>
              
              <div className="flex items-center justify-center">
                <input
                  id="remember-otp"
                  name="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-otp" className="ml-2 block text-sm text-gray-900">
                  Angemeldet bleiben (30 Tage)
                </label>
              </div>
              
              <button
                type="button"
                onClick={resetForm}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Andere E-Mail verwenden
              </button>
              
              <button
                type="button"
                onClick={handleRequestOtp}
                disabled={isLoading}
                className="w-full py-2 text-sm text-primary-600 hover:text-primary-700"
              >
                Code erneut senden
              </button>
            </div>
          </form>
        )}

        {step === 'password' && (
          <form className="mt-8 space-y-6" onSubmit={handlePasswordLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="sr-only">E-Mail</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none rounded-lg relative block w-full pl-10 px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-base sm:text-sm"
                    placeholder="E-Mail-Adresse"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="sr-only">Passwort</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none rounded-lg relative block w-full pl-10 px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-base sm:text-sm"
                    placeholder="Passwort"
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Anmelden'}
              </button>
            </div>
            
            <div className="flex items-center justify-center">
              <input
                id="remember-password"
                name="remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-password" className="ml-2 block text-sm text-gray-900">
                Angemeldet bleiben (30 Tage)
              </label>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={resetForm}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Zurück zur Code-Anmeldung
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
